import React, { useContext, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  RadioGroup,
  FormControl,
  Radio,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getMediaImage } from '../../api/aws/s3';
import { companiesRef } from '../../api/firebase/firestore/company';
import {
  addMedia,
  editMedia,
  mediasRef
} from '../../api/firebase/firestore/media';
import { mediaBasicShema } from '../../api/firebase/firestore/media/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import { sendMailMediaPublisher } from '../../api/sendgrid';
import noImage from '../../components/assets/noImage.svg';
import { genreChoice, funnel } from '../../constants/CheckboxList';
import {
  black,
  blackText,
  blueGrayText,
  cardBorder,
  primarycolor,
  gray,
  primaryButtonBackground,
  primaryButton
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  formWideMb,
  mypageHeader,
  mypageHeaderTitle,
  sizeBelowTenMb,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { publisherMediaRegisterEditSteps } from '../../constants/RegisterStep';
import { categoryList, mediaTypeList } from '../../constants/Select';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';
import { sendSlackMediaPublisher } from '../../modules/slack';

import CustomCheckbox from './CustomCheckbox';
import CustomImageUploader from './CustomImageUploader';
import CustomLoading from './CustomLoading';
import CustomNumberField from './CustomNumberField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';

import type { MediaType } from '../../api/firebase/firestore/media/mediaType';
import type { SelectOption } from '../../constants/Select';

const styles = {
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  circle: {
    bgcolor: '#DBDBE0',
    borderRadius: '50%',
    height: '64px',
    marginBottom: '10px',
    width: '64px'
  },
  flex: {
    display: 'flex'
  },
  formWideMb,
  grayPlaceholderText: {
    color: '#757575',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    marginBottom: '10px'
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    }
  },
  isRequiredText: {
    color: '#BD0017',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    margin: '0',
    marginLeft: '10px'
  },
  issueDescription: {
    color: blueGrayText,
    fontSize: '14px',
    marginBottom: '24px'
  },
  label: {
    fontSize: '12px'
  },
  mypageHeader,
  mypageHeaderTitle,
  nextButton: {
    '@media screen and (max-width: 480px)': {
      display: 'none'
    }
  },
  nextButtonSp: {
    '@media screen and (min-width: 481px)': {
      display: 'none'
    }
  },
  radiomarginLeft: {
    marginLeft: '5px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  skipButton: {
    height: '36px',
    width: '140px'
  },
  submit: {
    marginBottom: 0,
    marginTop: '32px',
    padding: 0
  },
  submitButton: {
    '@media screen and (max-width: 960px)': {
      height: '42px'
    },
    height: '42px'
  },
  text: {
    blackText,
    fontSize: '14px',
    mb: '10px',
    mt: '16px'
  },
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  uploadButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '36px',
    marginLeft: '11px',
    minWidth: '157px'
  }
};

export interface MediaBasicRegisterType {
  mediaName: string;
  logo: File;
  genre: boolean[];
  genreOther: string;
  overview: string;
  mediaType: number | null;
  funnel: boolean[];
  scalePvMau: string | null;
  scaleUu: string | null;
  scaleEmailSubscribers: string | null;
  scaleMembers: string | null;
  facebook: string | null;
  twitter: string | null;
  instagram: string | null;
  tiktok: string | null;
  note: string | null;
  youtube: string | null;
  line: string | null;
}

const MediaRegisterEditCard = (): React.ReactElement => {
  const { t } = useTranslation();
  const steps = publisherMediaRegisterEditSteps();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useContext(AuthContext);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [publisherCompanyId, setPublisherCompanyId] = useState<string>('');
  const [mediaCompanyId, setMediaCompanyId] = useState<string>('');
  const mTypeList = mediaTypeList();
  const cList = categoryList();

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    void (async () => {
      const userRef = doc(usersRef, currentUser?.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      if (userData) {
        const userCompany = userData.companyId as string;
        setPublisherCompanyId(userCompany);
        if (mediaCompanyId) {
          if (userCompany !== mediaCompanyId) {
            navigate('/auth/login');
          }
        }
      }
    })();
  }, [mediaCompanyId]);

  const {
    control,
    watch,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<MediaBasicRegisterType>({
    defaultValues: {
      facebook: null,
      instagram: null,
      line: null,
      mediaType: null,
      note: null,
      scaleEmailSubscribers: null,
      scaleMembers: null,
      scalePvMau: null,
      scaleUu: null,
      tiktok: null,
      twitter: null,
      youtube: null
    },
    resolver: yupResolver(mediaBasicShema)
  });

  const createCheckboxBooleanArray = (
    data?: number[],
    length?: number
  ): boolean[] => {
    if (!length || !data) return [];

    const newArray = Array(length).fill(false) as boolean[];
    data.forEach((element: number) => {
      newArray[element] = true;
    });

    return newArray;
  };

  useEffect(() => {
    void (async () => {
      if (id) {
        const mediaRef = doc(mediasRef, id);
        const mediaDoc = await getDoc(mediaRef);
        const mediaData = mediaDoc.data() as MediaType;
        const image = await getMediaImage(mediaData?.image);
        setPreviewLogo(image ?? noImage);
        setMediaCompanyId(mediaData?.companyId);
        reset({
          facebook: mediaData?.scaleSnsFollowers.Facebook
            ? String(mediaData?.scaleSnsFollowers.Facebook)
            : null,
          funnel: createCheckboxBooleanArray(
            mediaData?.funnel,
            watch('funnel')?.length
          ),
          genre: createCheckboxBooleanArray(
            mediaData?.category,
            watch('genre')?.length
          ),
          genreOther: mediaData?.categoryOther,
          instagram: mediaData?.scaleSnsFollowers.Instagram
            ? String(mediaData?.scaleSnsFollowers.Instagram)
            : null,
          line: mediaData?.scaleSnsFollowers.Line
            ? String(mediaData?.scaleSnsFollowers.Line)
            : null,
          mediaName: mediaData?.name,
          mediaType: mediaData?.mediaType,
          note: mediaData?.scaleSnsFollowers.note
            ? String(mediaData?.scaleSnsFollowers.note)
            : null,
          overview: mediaData?.overview,
          scaleEmailSubscribers: mediaData?.scaleEmailSubscribers
            ? String(mediaData?.scaleEmailSubscribers)
            : null,
          scaleMembers: mediaData?.scaleMembers
            ? String(mediaData?.scaleMembers)
            : null,
          scalePvMau: mediaData?.scalePvMau
            ? String(mediaData?.scalePvMau)
            : null,
          scaleUu: mediaData?.scaleUu ? String(mediaData?.scaleUu) : null,
          tiktok: mediaData?.scaleSnsFollowers.TikTok
            ? String(mediaData?.scaleSnsFollowers.TikTok)
            : null,
          twitter: mediaData?.scaleSnsFollowers.Twitter
            ? String(mediaData?.scaleSnsFollowers.Twitter)
            : null,
          youtube: mediaData?.scaleSnsFollowers.Youtube
            ? String(mediaData?.scaleSnsFollowers.Youtube)
            : null
        });
      }
    })();
  }, []);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('mediaName') === '' ||
        watch('genre') === null ||
        (watch('genre.99') && Boolean(!watch('genreOther'))) ||
        watch('overview') === null ||
        watch('mediaType') === null ||
        watch('funnel') === null ||
        watch('scalePvMau') === '' ||
        Boolean(errors.mediaName) ||
        Boolean(errors.logo) ||
        Boolean(errors.genre) ||
        (watch('genre.99') && Boolean(errors.genreOther)) ||
        Boolean(errors.overview) ||
        Boolean(errors.mediaType) ||
        Boolean(errors.funnel) ||
        Boolean(errors.scalePvMau) ||
        Boolean(errors.scaleUu) ||
        Boolean(errors.scaleEmailSubscribers) ||
        Boolean(errors.scaleMembers) ||
        Boolean(errors.facebook) ||
        Boolean(errors.twitter) ||
        Boolean(errors.instagram) ||
        Boolean(errors.tiktok) ||
        Boolean(errors.note) ||
        Boolean(errors.youtube) ||
        Boolean(errors.line) ||
        Boolean(logoSizeError) ||
        Boolean(scalePvMauError) ||
        Boolean(scaleUuError) ||
        Boolean(scaleEmailSubscribersError) ||
        Boolean(scaleMembersError) ||
        Boolean(facebookError) ||
        Boolean(twitterError) ||
        Boolean(instagramError) ||
        Boolean(tiktokError) ||
        Boolean(noteError) ||
        Boolean(youtubeError) ||
        Boolean(lineError)
      );
    }
    return false;
  };

  const convertToJapaneseNumber = (num: number): string => {
    if (num === 0) {
      return '';
    }
    let result = '';
    const formatNumber = (val: number, unit: string): string => {
      const formatted = val % 1 === 0 ? val.toString() : val.toFixed(1);
      return `${formatted}${unit}`;
    };

    if (num >= 1_0000_0000_0000) {
      result = formatNumber(num / 1_0000_0000_0000, '兆');
    } else if (num >= 1_0000_0000) {
      result = formatNumber(num / 1_0000_0000, '億');
    } else if (num >= 1_0000) {
      result = formatNumber(num / 1_0000, '万');
    } else {
      result = `${num}`;
    }
    return result;
  };

  const showValueToName = (
    selectOption: SelectOption[],
    itemValue: number | undefined
  ): string => {
    const d = selectOption.find((element) => element.value === itemValue);
    return d?.option ?? '';
  };

  const showMediaType = (v: number): string => {
    const datas = mTypeList.map((item) =>
      item.options.find((i) => i.value === v)
    );
    const d = datas.filter((item) => item !== undefined);
    return d[0]?.option ?? '';
  };

  const onSubmit = async (): Promise<void> => {
    setFirstSubmit(true);
    try {
      setLoading(true);

      if (!currentUser?.uid) {
        return;
      }

      if (!id) {
        const companyRef = doc(companiesRef, publisherCompanyId);
        const companyDoc = await getDoc(companyRef);

        const newMediaId = await addMedia(currentUser.uid, watch, s3FileLogo);
        const mediaRef = doc(mediasRef, newMediaId);
        const mediaDoc = await getDoc(mediaRef);
        const mediaData = mediaDoc.data() as MediaType;

        if (!companyDoc) {
          return;
        }

        const categoryArray = mediaData.category.map((item: number) =>
          showValueToName(cList, item)
        );
        const mediaType = showMediaType(mediaData.mediaType);

        sendSlackMediaPublisher(
          companyDoc,
          mediaDoc,
          categoryArray.join('、'),
          mediaType
        );

        await sendMailMediaPublisher(
          companyDoc,
          mediaDoc,
          categoryArray.join('、'),
          mediaType
        );

        setLoading(false);
        navigate(`/publisher/mypage/media/${newMediaId}/edit2`);
      } else {
        await editMedia(watch, id, s3FileLogo);
        navigate(`/publisher/mypage/media/${id}/edit2`);
      }
    } catch (e) {
      throwError(e);
    }
  };

  useEffect(() => {
    watch('genreOther') && setValue('genre.99', true);
  }, [watch('genreOther')]);

  useEffect(() => {
    !watch('genre.99') && setValue('genreOther', '');
  }, [watch('genre.99')]);

  const [previewLogo, setPreviewLogo] = useState('');
  const [s3FileLogo, setS3FileLogo] = useState<FileList | null>(null);
  const [logoSizeError, setLogoSizeError] = useState<string | null>(null);

  useEffect(() => {
    if (s3FileLogo && s3FileLogo.length > 0) {
      if (sizeBelowTenMb(s3FileLogo[0].size)) {
        setFirstSubmit(true);
        setLogoSizeError(t('validation.error.imageSize'));
      } else {
        setLogoSizeError(null);
      }
    } else {
      setLogoSizeError(null);
    }
  }, [s3FileLogo]);

  const changeUploadLogoFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreviewLogo(window.URL.createObjectURL(blob));
      setS3FileLogo(file);
    }
  };

  const optionsList = mediaTypeList();

  const getNameByMediaType = (mediaType: number): string | undefined => {
    for (const group of optionsList) {
      for (const option of group.options) {
        if (option.value === Number(mediaType)) {
          return group.name;
        }
      }
    }
    return undefined;
  };

  // 月間PV数のタイトルをメディア種別によって出し分け
  const pvTextSwitcher = (mediaType: number | null): string => {
    if (!mediaType) return t('media.scaleDetail.scalePvMau');
    const mediaTypeName = getNameByMediaType(mediaType);
    if (
      mediaTypeName === t('media.mediaTypeOption.tv') ||
      mediaTypeName === t('media.mediaTypeOption.radio')
    ) {
      return t('media.scaleDetail.sacleNet');
    } else if (
      mediaTypeName === t('media.mediaTypeOption.paper') ||
      mediaTypeName === t('media.mediaTypeOption.magazine')
    ) {
      return t('media.scaleDetail.scaleCirculation');
    } else if (mediaTypeName === t('media.mediaTypeOption.web')) {
      return t('media.scaleDetail.scalePvMau');
    }
    return t('media.scaleDetail.scaleOoh');
  };

  const [showUU, setShowUU] = useState<boolean>(true);
  const [uuText, setUuText] = useState<string>(t('media.scaleDetail.scaleUu'));
  // 月間UUをメディア種別によって出し分け
  useEffect(() => {
    const mediaTypeName = getNameByMediaType(watch('mediaType') ?? 0);
    if (
      mediaTypeName === t('media.mediaTypeOption.tv') ||
      mediaTypeName === t('media.mediaTypeOption.radio') ||
      mediaTypeName === t('media.mediaTypeOption.other')
    ) {
      setValue('scaleUu', null);
      setShowUU(false);
    } else if (
      mediaTypeName === t('media.mediaTypeOption.paper') ||
      mediaTypeName === t('media.mediaTypeOption.magazine')
    ) {
      setShowUU(true);
      setUuText(t('media.scaleDetail.scalReaders'));
    } else {
      setShowUU(true);
      setUuText(t('media.scaleDetail.scaleUu'));
    }
  }, [watch('mediaType')]);

  // 媒体規模1兆までのバリデーション
  const [scalePvMauError, setScalePvMauError] = useState<string | null>(null);
  const [scaleUuError, setScaleUuError] = useState<string | null>(null);
  const [scaleMembersError, setScaleMembersError] = useState<string | null>(
    null
  );
  const [scaleEmailSubscribersError, setScaleEmailSubscribersError] = useState<
    string | null
  >(null);
  const [facebookError, setFacebookError] = useState<string | null>(null);
  const [twitterError, setTwitterError] = useState<string | null>(null);
  const [instagramError, setInstagramError] = useState<string | null>(null);
  const [tiktokError, setTiktokError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [lineError, setLineError] = useState<string | null>(null);

  const numberValidationCheck = (title: string): boolean => {
    switch (title) {
      case 'scalePvMau':
        if (
          watch('scalePvMau') === null ||
          watch('scalePvMau') === undefined ||
          Number.isNaN(watch('scalePvMau'))
        )
          return false;
        if (Number(watch('scalePvMau')) >= 1000000000000) {
          setScalePvMauError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('scalePvMau')) < 0) {
          setScalePvMauError(t('validation.error.above1'));
          return true;
        }
        setScalePvMauError(null);
        return false;
      case 'scaleUu':
        if (
          watch('scaleUu') === null ||
          watch('scaleUu') === undefined ||
          Number.isNaN(watch('scaleUu'))
        )
          return false;
        if (Number(watch('scaleUu')) >= 1000000000000) {
          setScaleUuError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('scaleUu')) < 0) {
          setScaleUuError(t('validation.error.above1'));
          return true;
        }
        setScaleUuError(null);
        return false;
      case 'scaleMembers':
        if (
          watch('scaleMembers') === null ||
          watch('scaleMembers') === undefined ||
          Number.isNaN(watch('scaleMembers'))
        )
          return false;
        if (Number(watch('scaleMembers')) >= 1000000000000) {
          setScaleMembersError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('scaleMembers')) < 0) {
          setScaleMembersError(t('validation.error.above1'));
          return true;
        }
        setScaleMembersError(null);
        return false;
      case 'scaleEmailSubscribers':
        if (
          watch('scaleEmailSubscribers') === null ||
          watch('scaleEmailSubscribers') === undefined ||
          Number.isNaN(watch('scaleEmailSubscribers'))
        )
          return false;
        if (Number(watch('scaleEmailSubscribers')) >= 1000000000000) {
          setScaleEmailSubscribersError(
            t('validation.error.below1000000000000')
          );
          return true;
        } else if (Number(watch('scaleEmailSubscribers')) < 0) {
          setScaleEmailSubscribersError(t('validation.error.above1'));
          return true;
        }
        setScaleEmailSubscribersError(null);
        return false;
      case 'facebook':
        if (
          watch('facebook') === null ||
          watch('facebook') === undefined ||
          Number.isNaN(watch('facebook'))
        )
          return false;
        if (Number(watch('facebook')) >= 1000000000000) {
          setFacebookError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('facebook')) < 0) {
          setFacebookError(t('validation.error.above1'));
          return true;
        }
        setFacebookError(null);
        return false;
      case 'twitter':
        if (
          watch('twitter') === null ||
          watch('twitter') === undefined ||
          Number.isNaN(watch('twitter'))
        )
          return false;
        if (Number(watch('twitter')) >= 1000000000000) {
          setTwitterError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('twitter')) < 0) {
          setTwitterError(t('validation.error.above1'));
          return true;
        }
        setTwitterError(null);
        return false;
      case 'instagram':
        if (
          watch('instagram') === null ||
          watch('instagram') === undefined ||
          Number.isNaN(watch('instagram'))
        )
          return false;
        if (Number(watch('instagram')) >= 1000000000000) {
          setInstagramError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('instagram')) < 0) {
          setInstagramError(t('validation.error.above1'));
          return true;
        }
        setInstagramError(null);
        return false;
      case 'tiktok':
        if (
          watch('tiktok') === null ||
          watch('tiktok') === undefined ||
          Number.isNaN(watch('tiktok'))
        )
          return false;
        if (Number(watch('tiktok')) >= 1000000000000) {
          setTiktokError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('tiktok')) < 0) {
          setTiktokError(t('validation.error.below1000000000000'));
          t('validation.error.above1');
          return true;
        }
        setTiktokError(null);
        return false;
      case 'note':
        if (
          watch('note') === null ||
          watch('note') === undefined ||
          Number.isNaN(watch('note'))
        )
          return false;
        if (Number(watch('note')) >= 1000000000000) {
          setNoteError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('note')) < 0) {
          setNoteError(t('validation.error.above1'));
          return true;
        }
        setNoteError(null);
        return false;
      case 'youtube':
        if (
          watch('youtube') === null ||
          watch('youtube') === undefined ||
          Number.isNaN(watch('youtube'))
        )
          return false;
        if (Number(watch('youtube')) >= 1000000000000) {
          setYoutubeError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('youtube')) < 0) {
          setYoutubeError(t('validation.error.above1'));
          return true;
        }
        setYoutubeError(null);
        return false;
      case 'line':
        if (
          watch('line') === null ||
          watch('line') === undefined ||
          Number.isNaN(watch('line'))
        )
          return false;
        if (Number(watch('line')) >= 1000000000000) {
          setLineError(t('validation.error.below1000000000000'));
          return true;
        } else if (Number(watch('line')) < 0) {
          setLineError(t('validation.error.above1'));
          return true;
        }
        setLineError(null);
        return false;
      default:
        return false;
    }
  };

  useEffect(() => {
    firstSubmit && numberValidationCheck('scalePvMau');
  }, [watch('scalePvMau')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('scaleUu');
  }, [watch('scaleUu')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('scaleMembers');
  }, [watch('scaleMembers')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('scaleEmailSubscribers');
  }, [watch('scaleEmailSubscribers')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('facebook');
  }, [watch('facebook')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('twitter');
  }, [watch('twitter')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('instagram');
  }, [watch('instagram')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('tiktok');
  }, [watch('tiktok')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('note');
  }, [watch('note')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('youtube');
  }, [watch('youtube')]);

  useEffect(() => {
    firstSubmit && numberValidationCheck('line');
  }, [watch('line')]);

  return (
    <>
      <Typography
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'static',
          width: '100%'
        }}
      >
        <Typography sx={styles.mypageHeader} component="div">
          <Typography sx={styles.mypageHeaderTitle}>
            {t('mypage.mediaRegister')}
          </Typography>
          <Button
            disabled={buttonDisabledFlag()}
            variant="contained"
            sx={[styles.nextButton, primaryButton]}
            onClick={() => {
              void (async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => await onSubmit())();
              })();
            }}
          >
            {t('publisherMediaRegisterEdit.button')}
          </Button>
          <Button
            disabled={buttonDisabledFlag()}
            variant="contained"
            sx={[styles.nextButtonSp, primaryButton]}
            onClick={() => {
              void (async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => await onSubmit())();
              })();
            }}
          >
            {t('publisherMediaRegisterEdit.spButton')}
          </Button>
        </Typography>
        <CustomLoading loading={loading} />
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ marginBottom: '24px', marginTop: '24px' }}
        >
          <Grid>
            <Card sx={[styles.cardSize, styles.cardBorder]}>
              <CardContent sx={styles.cardContentPadding}>
                <CustomStepper steps={steps} activeStep={0} />
                <Typography
                  component="div"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}
                >
                  <Typography sx={styles.title} component="div">
                    {t('publisherMediaRegisterEdit.step1')}
                  </Typography>
                </Typography>

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('publisherMediaRegisterEdit.mediaName')}
                      requiredText={t('form.required')}
                      isRequired
                      error={Boolean(errors.mediaName)}
                      helperText={errors.mediaName?.message}
                    />
                  )}
                  control={control}
                  name="mediaName"
                />
                <Controller
                  render={({ field }) => (
                    <CustomImageUploader
                      {...field}
                      type="mediaLogo"
                      preview={previewLogo}
                      error={Boolean(errors.logo)}
                      otherError={logoSizeError}
                      helperText={errors.logo?.message}
                      onChange={(
                        value: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        field.onChange(value);
                        changeUploadLogoFile(value);
                      }}
                    />
                  )}
                  control={control}
                  name="logo"
                />
                <CustomCheckbox
                  labelText={t('publisherMediaRegisterEdit.genre')}
                  isRequired
                  requiredText={t('form.required')}
                  list={genreChoice()}
                  control={control}
                  registerMedia={register}
                  watchMedia={watch}
                  error={Boolean(errors.genre)}
                  helperText={errors.genre?.message}
                  formMb="narrow"
                  name="genre"
                />
                <Typography component="div" sx={{ marginLeft: '25px' }}>
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        multiline
                        rows={3}
                        fieldType="textareaRow3"
                        width="other"
                        error={watch('genre.99') && Boolean(errors.genreOther)}
                        helperText={errors.genreOther?.message}
                      />
                    )}
                    control={control}
                    name="genreOther"
                  />
                </Typography>
                <Controller
                  render={({ field }) => (
                    <Typography align="left" component="div">
                      <Typography component="div" sx={styles.flex}>
                        <Typography sx={styles.grayPlaceholderText}>
                          {t('publisherMediaRegisterEdit.media')}
                        </Typography>
                        <Typography sx={styles.isRequiredText}>
                          {t('form.required')}
                        </Typography>
                      </Typography>
                      <FormHelperText
                        sx={styles.helper}
                        error={Boolean(errors.mediaType)}
                      >
                        {errors.mediaType?.message}
                      </FormHelperText>
                      <FormControl>
                        <RadioGroup
                          {...field}
                          aria-labelledby="demo-form-control-label-placement"
                          name="position"
                          sx={{
                            '&.MuiFormGroup-root': {
                              alignContent: 'flex-start',
                              marginBottom: '10px'
                            }
                          }}
                        >
                          <Grid container flexDirection={'column'}>
                            <Typography sx={styles.text}>
                              {t('media.mediaTypeOption.tv')}
                            </Typography>
                            <FormControlLabel
                              value={41}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.41')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={42}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.42')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={43}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.43')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={44}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.44')}
                              sx={blackText}
                            />
                          </Grid>
                          <Grid container flexDirection={'column'}>
                            <Typography sx={styles.text}>
                              {t('media.mediaTypeOption.radio')}
                            </Typography>
                            <FormControlLabel
                              value={51}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.51')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={52}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.52')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={53}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.53')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={54}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.54')}
                              sx={blackText}
                            />
                          </Grid>
                          <Grid container flexDirection={'column'}>
                            <Typography sx={styles.text}>
                              {t('media.mediaTypeOption.web')}
                            </Typography>
                            <FormControlLabel
                              value={61}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.61')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={62}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.62')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={66}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.66')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={67}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.67')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={63}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.63')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={64}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.64')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={65}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.65')}
                              sx={blackText}
                            />
                          </Grid>
                          <Grid container flexDirection={'column'}>
                            <Typography sx={styles.text}>
                              {t('media.mediaTypeOption.paper')}
                            </Typography>
                            <FormControlLabel
                              value={71}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.71')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={72}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.72')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={73}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.73')}
                              sx={blackText}
                            />
                          </Grid>
                          <Grid container flexDirection={'column'}>
                            <Typography sx={styles.text}>
                              {t('media.mediaTypeOption.magazine')}
                            </Typography>
                            <FormControlLabel
                              value={21}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.21')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={22}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.22')}
                              sx={blackText}
                            />
                            <FormControlLabel
                              value={23}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.23')}
                              sx={blackText}
                            />
                          </Grid>
                          <Grid container flexDirection={'column'}>
                            <Typography sx={styles.text}>
                              {t('industry.option99')}
                            </Typography>
                            <FormControlLabel
                              value={31}
                              control={<Radio sx={{ color: gray }} />}
                              label={t('media.mediaTypeOption.31')}
                              sx={blackText}
                            />
                          </Grid>
                        </RadioGroup>
                      </FormControl>
                    </Typography>
                  )}
                  control={control}
                  name="mediaType"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired
                      requiredText={t('form.required')}
                      labelText={t('media.overview')}
                      multiline
                      rows={8}
                      fieldType="textareaRow8"
                      width="article"
                      error={Boolean(errors.overview)}
                      helperText={errors.overview?.message}
                      guideText={t('guideText.registerMedia.overview')}
                    />
                  )}
                  control={control}
                  name="overview"
                />
                <CustomCheckbox
                  labelText={t('publisherMediaRegisterEdit.fannel')}
                  isRequired={true}
                  requiredText={t('form.required')}
                  list={funnel()}
                  control={control}
                  registerMedia={register}
                  watchMedia={watch}
                  error={Boolean(errors.funnel)}
                  helperText={errors.funnel?.message}
                  formMb="narrow"
                  name="funnel"
                  guideText={t('guideText.registerMedia.funnel')}
                />
                <Typography align="left" component="div" sx={{ mt: '24px' }}>
                  <Typography component={'div'} sx={styles.flex}>
                    <Typography sx={styles.grayPlaceholderText}>
                      {t('publisherMediaRegisterEdit.scale')}
                    </Typography>
                  </Typography>

                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        labelText={pvTextSwitcher(watch('mediaType'))}
                        isRequired
                        requiredText={t('form.required')}
                        underText={
                          !errors.scalePvMau &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.scalePvMau)}
                        helperText={errors.scalePvMau?.message}
                        otherError={scalePvMauError}
                      />
                    )}
                    control={control}
                    name="scalePvMau"
                  />
                  {showUU && (
                    <Controller
                      render={({ field }) => (
                        <CustomNumberField
                          {...field}
                          labelText={uuText}
                          isRequired
                          underText={
                            !errors.scaleUu &&
                            field.value !== null &&
                            field.value !== ''
                              ? convertToJapaneseNumber(
                                  Number(
                                    String(field.value).replace(/[^\d]/g, '')
                                  )
                                )
                              : ''
                          }
                          error={Boolean(errors.scaleUu)}
                          helperText={errors.scaleUu?.message}
                          otherError={scaleUuError}
                        />
                      )}
                      control={control}
                      name="scaleUu"
                    />
                  )}
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        labelText={t('media.scaleDetail.scaleMembers')}
                        isRequired
                        underText={
                          !errors.scaleMembers &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.scaleMembers)}
                        helperText={errors.scaleMembers?.message}
                        otherError={scaleMembersError}
                      />
                    )}
                    control={control}
                    name="scaleMembers"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        labelText={t('media.scaleDetail.scaleEmailSubscribers')}
                        isRequired
                        underText={
                          !errors.scaleEmailSubscribers &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.scaleEmailSubscribers)}
                        helperText={errors.scaleEmailSubscribers?.message}
                        otherError={scaleEmailSubscribersError}
                      />
                    )}
                    control={control}
                    name="scaleEmailSubscribers"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        labelText={t('media.sns')}
                        requiredText={t('form.required')}
                        isRequired={false}
                        placeholder="facebook"
                        formMb="narrow"
                        underText={
                          !errors.facebook &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.facebook)}
                        helperText={errors.facebook?.message}
                        otherError={facebookError}
                      />
                    )}
                    control={control}
                    name="facebook"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        isRequired={false}
                        placeholder="Twitter"
                        formMb="narrow"
                        underText={
                          !errors.twitter &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.twitter)}
                        helperText={errors.twitter?.message}
                        otherError={twitterError}
                      />
                    )}
                    control={control}
                    name="twitter"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        isRequired={false}
                        placeholder="Instagram"
                        formMb="narrow"
                        underText={
                          !errors.instagram &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.instagram)}
                        helperText={errors.instagram?.message}
                        otherError={instagramError}
                      />
                    )}
                    control={control}
                    name="instagram"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        isRequired={false}
                        placeholder="TikTok"
                        formMb="narrow"
                        underText={
                          !errors.tiktok &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.tiktok)}
                        helperText={errors.tiktok?.message}
                        otherError={tiktokError}
                      />
                    )}
                    control={control}
                    name="tiktok"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        isRequired={false}
                        placeholder="note"
                        formMb="narrow"
                        underText={
                          !errors.note &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.note)}
                        helperText={errors.note?.message}
                        otherError={noteError}
                      />
                    )}
                    control={control}
                    name="note"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        isRequired={false}
                        placeholder="Youtube"
                        formMb="narrow"
                        underText={
                          !errors.note &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.youtube)}
                        helperText={errors.youtube?.message}
                        otherError={youtubeError}
                      />
                    )}
                    control={control}
                    name="youtube"
                  />
                  <Controller
                    render={({ field }) => (
                      <CustomNumberField
                        {...field}
                        isRequired={false}
                        placeholder="LINE"
                        formMb="narrow"
                        underText={
                          !errors.note &&
                          field.value !== null &&
                          field.value !== ''
                            ? convertToJapaneseNumber(
                                Number(
                                  String(field.value).replace(/[^\d]/g, '')
                                )
                              )
                            : ''
                        }
                        error={Boolean(errors.line)}
                        helperText={errors.line?.message}
                        otherError={lineError}
                      />
                    )}
                    control={control}
                    name="line"
                  />
                </Typography>

                <CardActions sx={[styles.textMb, styles.submit]}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        disabled={buttonDisabledFlag()}
                        sx={[primaryButtonBackground, styles.submitButton]}
                        onClick={() => {
                          void (async () => {
                            setFirstSubmit(true);
                            await handleSubmit(async () => {
                              const scalePvMauCheck =
                                numberValidationCheck('scalePvMau');
                              const scaleUuCheck =
                                numberValidationCheck('scaleUu');
                              const scaleMemebersCheck =
                                numberValidationCheck('scaleMembers');
                              const scaleEmailSubscribersCheck =
                                numberValidationCheck('scaleEmailSubscribers');
                              const facebookCheck =
                                numberValidationCheck('facebook');
                              const twitterCheck =
                                numberValidationCheck('twitter');
                              const instagramCheck =
                                numberValidationCheck('instagram');
                              const tiktokCheck =
                                numberValidationCheck('tiktok');
                              const noteCheck = numberValidationCheck('note');
                              const youtubeCheck =
                                numberValidationCheck('youtube');
                              const lineCheck = numberValidationCheck('line');

                              if (
                                !scalePvMauCheck &&
                                !scaleUuCheck &&
                                !scaleMemebersCheck &&
                                !scaleEmailSubscribersCheck &&
                                !facebookCheck &&
                                !twitterCheck &&
                                !instagramCheck &&
                                !tiktokCheck &&
                                !noteCheck &&
                                !youtubeCheck &&
                                !lineCheck
                              ) {
                                await onSubmit();
                              }
                            })();
                          })();
                        }}
                      >
                        <Typography
                          component="div"
                          color="white"
                          sx={{ fontSize: 15 }}
                        >
                          {t('publisherMediaRegisterEdit.button')}
                        </Typography>
                      </Button>
                    </Grid>
                  </Grid>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Typography>
    </>
  );
};
export default MediaRegisterEditCard;
